import * as React from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Pagination } from '@/components/common/Pagination';
import { ColumnVisibility } from '@/components/common/ColumnVisibility';
import { FilterToggleButton } from '@/components/common/CollapsibleFilters';
import { TableHeaderControls } from '@/components/common/TableHeaderControls';
import { TableZoomControls } from '@/components/common/TableZoomControls';
import { useColumnVisibility } from '@/lib/hooks/useColumnVisibility';
import { useTableZoom } from '@/lib/hooks/useTableZoom';
import { useDebounce } from '@/lib/hooks/useDebounce';
import type { Matter, MatterFilters } from '../types';
import { useMatterColumns } from './MatterColumns';
import { MattersFilterBar } from './MattersFilterBar';

interface MattersDataTableProps {
  data: Matter[];
  total: number;
  filters: MatterFilters;
  isLoading?: boolean;
  onFiltersChange: <K extends keyof MatterFilters>(key: K, value: MatterFilters[K]) => void;
  onFiltersChangeBatch?: (updates: Partial<MatterFilters>) => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onSort: (column: string) => void;
  onEdit?: (matter: Matter) => void;
  onDelete?: (matter: Matter) => void;
  onNps?: (matter: Matter) => void;
  onRateTeam?: (matter: Matter) => void;
  /** Open the Quality Gate dialog for the matter's next lifecycle move. */
  onAdvance?: (matter: Matter) => void;
  hasAccess?: (resource: string, action: string) => boolean;
  onClearFilters?: () => void;
  /** Page-header slot for the Columns/Filters buttons (TableHeaderControls). */
  controlsContainer?: HTMLElement | null;
}

export function MattersDataTable({
  data,
  total,
  filters,
  isLoading,
  onFiltersChange,
  onFiltersChangeBatch,
  onPageChange,
  onLimitChange,
  onSort,
  onEdit,
  onDelete,
  onNps,
  onRateTeam,
  onAdvance,
  hasAccess,
  onClearFilters,
  controlsContainer,
}: MattersDataTableProps) {
  const { t } = useTranslation();
  const [columnVisibility, setColumnVisibility] = useColumnVisibility('matters');
  const tableZoom = useTableZoom();
  const [rowSelection, setRowSelection] = React.useState({});
  const [filtersOpen, setFiltersOpen] = React.useState(false);

  const [searchValue, setSearchValue] = React.useState(filters.s ?? '');
  const debouncedSearch = useDebounce(searchValue, 300);
  const isFirstRender = React.useRef(true);

  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const trimmed = debouncedSearch.trim();
    const newValue = trimmed.length >= 2 ? trimmed : undefined;
    if (newValue !== filters.s) {
      onFiltersChange('s', newValue);
    }
  }, [debouncedSearch]);

  const columns = useMatterColumns({
    onEdit,
    onDelete,
    onNps,
    onRateTeam,
    onAdvance,
    hasAccess,
    orderBy: filters.orderBy,
    order: filters.order,
    onSort,
  });

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: { columnVisibility, rowSelection },
  });

  const currentPage = filters.page ?? 0;
  const pageSize = filters.limit ?? 10;

  const activeFilterCount = React.useMemo(() => {
    let count = 0;
    if (filters.clientId) count++;
    if (filters.contractId) count++;
    if (filters.phase) count++;
    if (filters.phases) count++;
    if (filters.result) count++;
    if (filters.compliance) count++;
    if (filters.s) count++;
    return count;
  }, [filters]);

  const handleClearFilters = () => {
    setSearchValue('');
    onClearFilters?.();
  };

  return (
    <div className="w-full space-y-4">
      {/* Matter Type Toggle */}
      <ToggleGroup
        type="single"
        size="sm"
        className="justify-start gap-1"
        value={filters.isCourtCase === undefined ? 'none' : filters.isCourtCase.toString()}
        onValueChange={(item) =>
          onFiltersChange('isCourtCase', item === 'none' ? undefined : item === 'true')
        }
      >
        <ToggleGroupItem
          value="none"
          aria-label="All"
          className="rounded-full border px-4 data-[state=on]:bg-primary data-[state=on]:text-white"
        >
          {t('all')}
        </ToggleGroupItem>
        <ToggleGroupItem
          value="true"
          aria-label="Legal Case"
          className="group/toggle rounded-full border px-4 data-[state=on]:bg-primary data-[state=on]:text-white"
        >
          <img src="/images/legal.svg" alt="" className="inline mr-1.5 h-4 w-4 group-data-[state=on]/toggle:brightness-0 group-data-[state=on]/toggle:invert" />
          {t('legalCase')}
        </ToggleGroupItem>
        <ToggleGroupItem
          value="false"
          aria-label="Project"
          className="group/toggle rounded-full border px-4 data-[state=on]:bg-primary data-[state=on]:text-white"
        >
          <img src="/images/project.svg" alt="" className="inline mr-1.5 h-4 w-4 group-data-[state=on]/toggle:brightness-0 group-data-[state=on]/toggle:invert" />
          {t('project')}
        </ToggleGroupItem>
      </ToggleGroup>

      {/* Table Actions Bar */}
      <div className="flex items-center justify-between px-2">
        <div className="text-sm text-muted-foreground">
          {total > 0 && (
            <span>
              {t('showing')}{' '}
              {Math.min(currentPage * pageSize + 1, total)}-{Math.min((currentPage + 1) * pageSize, total)}{' '}
              {t('of')} {total}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <TableHeaderControls container={controlsContainer}>
            <ColumnVisibility table={table} />
            <FilterToggleButton
              activeFilterCount={activeFilterCount}
              open={filtersOpen}
              onToggle={() => setFiltersOpen((v) => !v)}
            />
          </TableHeaderControls>
        </div>
      </div>

      <MattersFilterBar
        open={filtersOpen}
        filters={filters}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onFiltersChange={onFiltersChange}
        activeFilterCount={activeFilterCount}
        onClearFilters={handleClearFilters}
      />

      {/* Table */}
      <div className="rounded-lg border border-[#e2e8f0] bg-white overflow-hidden">
        <Table style={{ zoom: tableZoom.zoom }}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="bg-[#f8fafc] hover:bg-[#f8fafc]">
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className="text-[11px] font-semibold uppercase tracking-wider text-[#64748b]">
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {columns.map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : data?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className="hover:bg-[#f8fafc]"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <div className="py-8">
                    <h3 className="mb-2 text-xl font-medium text-[#1a2b4a]">{t('noMatterFound')}</h3>
                    <p className="text-[#64748b]">{t('trackMatterToManageFirm')}</p>
                    {hasAccess?.('matter', 'create') && (
                      <Link to="/app/matters/new">
                        <Button className="mt-4">{t('newMatter')}</Button>
                      </Link>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination
        leadingControls={<TableZoomControls zoom={tableZoom} />}
        page={filters.page ?? 0}
        limit={filters.limit ?? 10}
        total={total}
        onPageChange={onPageChange}
        onLimitChange={onLimitChange}
        isLoading={isLoading}
      />
    </div>
  );
}
